import { ReactElement } from "react";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
} from "lexical";
// @ts-ignore
import { $setBlocksType } from "@lexical/selection";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingTagType,
} from "@lexical/rich-text";
import { blockTypeToBlockName } from "../constants";
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import FormatListBulletedIcon from "@ant-design/icons/UnorderedListOutlined";
import FormatListNumberedOutlinedIcon from "@ant-design/icons/OrderedListOutlined";
import { $createCodeNode } from "@lexical/code";
import { UseBlockFormatProps } from "../types";
import { $createLinkNode } from "@lexical/link";

const icon: React.CSSProperties = {
  color: "black",
  cursor: "pointer",
  fontSize: "16px",
};
const useBlockFormat = (props: UseBlockFormatProps) => {
  const { blockType } = props;
  const [editor] = useLexicalComposerContext();

  const formatParagraph = () => {
    if (blockType !== "paragraph") {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection))
          $setBlocksType(selection, () => $createParagraphNode());
      });
    }
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatCheckList = () => {
    if (blockType !== "check") {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatQuote = () => {
    if (blockType !== "quote") {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const formatCode = (language: string) => {
    if (blockType !== "code") {
      editor.update(() => {
        let selection = $getSelection();

        if ($isRangeSelection(selection)) {
          if (selection.isCollapsed()) {
            $setBlocksType(selection, () => $createCodeNode(language));
          } else {
            const textContent = selection.getTextContent();
            const codeNode = $createCodeNode(language);
            selection.insertNodes([codeNode]);
            selection = $getSelection();
            if ($isRangeSelection(selection))
              selection.insertRawText(textContent);
          }
        }
      });
    }
  };

  const insertLink = () => {
    if (blockType !== "code") {
      editor.update(() => {
        let selection = $getSelection();

        if ($isRangeSelection(selection)) {
          if (selection.isCollapsed()) {
            // If the selection is collapsed, insert a link node
            const linkNode = $createLinkNode(""); // You need to implement $createLinkNode function
            selection.insertNodes([linkNode]);
          } else {
            // If the selection is not collapsed, wrap the selected text with a link
            const textContent = selection.getTextContent();
            const linkNode = $createLinkNode(""); // You need to implement $createLinkNode function
            selection.insertNodes([linkNode]);
            selection = $getSelection();
            if ($isRangeSelection(selection))
              selection.insertRawText(textContent);
          }
        }
      });
    }
  };

  interface Block {
    name: string;
    blockType: keyof typeof blockTypeToBlockName;
    icon: ReactElement<any>;
    onClick: () => void;
  }

  const blocks: Block[] = [
    {
      name: "Bulleted List",
      blockType: "bullet",
      icon: <FormatListBulletedIcon style={icon} />,
      onClick: formatBulletList,
    },
    {
      name: "Numbered List",
      blockType: "number",
      icon: <FormatListNumberedOutlinedIcon style={icon} />,
      onClick: formatNumberedList,
    },
  ];

  return {
    blocks,
    formatParagraph,
    formatHeading,
    formatBulletList,
    formatCheckList,
    formatNumberedList,
    formatQuote,
    formatCode,
    insertLink,
  };
};

export default useBlockFormat;
